import * as React from "react";
import { MoneyDisplayHelper } from "../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { Accordion } from "../../../../../../../react-core/Html/Accordion";
import { JsonDateParser } from "../../../../../../../react-core/Utils/JsonDateParser";
import { PensionOption } from "../../../../../../../Wrap/Constants/Enums/Apply/PensionOption";
import { DrawdownType } from "../../../../../../../Wrap/Constants/Enums/Drawdown/DrawdownType";
import { MoneyOrPercentageType } from "../../../../../../../Wrap/Constants/Enums/MoneyOrPercentageType";
import { ApplyModel } from "../../../../../../../Wrap/Models/Models/Processes/Apply/ApplyModel";
import { PensionBase } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/PensionBase";
import { ContributionBase } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/Shared/ContributionBase";
import { ProcessType } from "../../../../../../Shared/IST/Resource/Content";
import { Typography } from "../../../../../../Shared/Typography/Typography";
import { LessThanAge } from "../../Helpers/PensionHelper";
import { FrequencyArraysMap, FrequencyTypeArrays, SourceTypeMap } from "../../Resource/Contents";
import { CrystallizationPerContribution } from "../../SIPP/DrawdownDetails/CrystallizationPerContribution";
import {
    calculateAvailableToCrystallise,
    calculateUnCrystallizationAmount,
    getAllPensionTransfersInvestments,
    isCappedOrFlexIOrUFPLS,
    isRegualr,
    showBenefitTable,
} from "../../SIPP/DrawdownDetails/Helper/DrawdownDetailHelper";
import {
    AllowanceDetailHeading,
    CrystallisationOptionsMap,
    DrawdownTypeOptionsMap,
    LTADetailHeading,
    PensionOptionMap,
    PensionPathwayInvestmentOptionArrayMap,
} from "../../SIPP/DrawdownDetails/Resource/Contents";
import { bankAccountParseWithHolderName, boolParse, dateParser, prepareTypography } from "../ReviewParser";
import { CurrencyCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { ExistingContributionStore, ExistingRegularCryStore } from "../../../EditIncomeDrawdownWizard/Stores/Variable";
import { CustomerPensionProtectionModel } from "../../../../../../../Wrap/Models/Domains/Drawdown/CustomerPensionProtectionModel";
import { TaxYearHelper } from "../../Helpers/TaxYearHelper";
import { SwitchToAllocationTable } from "../../SIPP/DrawdownDetails/SwitchToAllocationTable";
import { SwitchInvestmentsTable } from "./SwitchInvestmentsTable";
import { PensionPathwayInvestmentOption } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/Shared/PensionPathwayInvestmentOption";
import { investmentPathOptionsMap } from '../../../../../../Shared/IST/Resource/Content';
import { SourceType } from "../../../../../../../Wrap/Constants/Enums/Domain/SourceType";
import { ChargeFromMap } from "../../AdviserCharges/Resource/Contents";

interface IDrawdownReviewProps {
    data: PensionBase;
    process: ProcessType;
    colNum: number;
    onEditAction: () => void;
    isOpen?: boolean;
    showDrawdownPriceLock?: boolean;
    showAssetsToSell?: boolean;
    modelData: ContributionBase | ApplyModel;
    isImmediateLumpSum: boolean;
    hasEnhancedPensionProtection: boolean;
}


export const DrawdownReview = (props: IDrawdownReviewProps) => {
    const { data, process, modelData, hasEnhancedPensionProtection } = props;
    const lessThan75 = LessThanAge(75, JsonDateParser.toDate(modelData.ClientDetails?.DateOfBirth));

    const existingContribution = ExistingContributionStore.getExistingContribution()?.Contribution;
    const existingregularcrystallisationdetails = ExistingRegularCryStore.getExistingRegularCry();

    const showOffPlatformBCE = (pensionProtection: CustomerPensionProtectionModel) => {
        return !(hasEnhancedProtection(pensionProtection) && hasEnhancedPCLSProtection(pensionProtection));
    };

    const drawdownConfig = (contribution) => {
        if (!contribution) { return []; }
        const drawdown = [];
        if (process == ProcessType.TakePensionBenefits || process == ProcessType.TopUpWithITFLS || process == ProcessType.NBEC || process == ProcessType.NB) {
            const adviceGiven = process == ProcessType.NB || process == ProcessType.TopUpWithITFLS ? contribution?.DrawdownModel?.InvestmentPathwayAnswers?.PersonalRecommendation : contribution?.AdviceGiven;

            if (!(
                (process === ProcessType.TakePensionBenefits || process === ProcessType.TopUpWithITFLS) &&
                !contribution?.DrawdownModel?.PreADay &&
                contribution?.DrawdownModel?.PensionOption == PensionOption.UFPLS
            )) {
                drawdown.push(
                    {
                        title: "Did you give financial advice?",
                        data: boolParse(adviceGiven),
                    }
                );
               
                if(!adviceGiven){
                drawdown.push(
                    {
                        title: "How does the client wish to proceed with the assets in the pension?",
                        data: PensionPathwayInvestmentOptionArrayMap[contribution?.DrawdownModel?.InvestmentPathwayAnswers?.InvestmentOption],
                    });
                }    

            }

            if ((process == ProcessType.TakePensionBenefits || process == ProcessType.TopUpWithITFLS) && !adviceGiven) {

                if (contribution?.DrawdownModel?.InvestmentPathwayAnswers?.InvestmentOption == PensionPathwayInvestmentOption.InvestmentPathway) {
                    drawdown.push(
                        {
                            title: "Which plan best reflects your investment aims?",
                            data: investmentPathOptionsMap[contribution?.DrawdownModel?.InvestmentPathwayAnswers?.SelectedPathwayId],
                        });

                    drawdown.push(
                        {
                            title: "Take up this investment pathway choice?",
                            data: boolParse(contribution?.DrawdownModel?.InvestmentPathwayAnswers?.TakeUpInvestmentPathwayChoice),
                        });

                }
            };

            drawdown.push(
                {
                    title:
                        "Does your client have pension, drawdown or annuity that started before 6 April 2006 that you have not informed us of previously?",
                    data: boolParse(contribution?.DrawdownModel?.PreADay),
                },
                {
                    title: "Pension option",
                    data: PensionOptionMap[contribution?.DrawdownModel?.PensionOption],
                }
            );

            if (!contribution?.DrawdownModel?.PreADay) {
                if (![ProcessType.NB, ProcessType.TopUpWithITFLS].includes(process)) {
                    drawdown.push(
                        {
                            title:
                                "Does your client have any form of pension protection?",
                            data: boolParse(contribution?.HasPensionProtection),
                        }
                    );
                }
                if (showOffPlatformBCE(contribution.PensionProtection)) {
                    drawdown.push(
                        {
                            title:
                                "Have there been any Benefit Crystallisation Events (BCEs) off platform prior to 6 April 2024?",
                            data: boolParse(contribution?.DrawdownModel?.AllowanceDetails?.HasOffPlatformBcePriorApril2024),
                        }
                    );
                    if (contribution?.DrawdownModel?.AllowanceDetails?.HasOffPlatformBcePriorApril2024) {
                        drawdown.push(
                            {
                                title:
                                    "Do you know the allowance amounts for these BCEs?",
                                data: boolParse(hasTransitionalTaxFreeAllowanceCertificate(contribution.PensionProtection) ? true : contribution?.DrawdownModel?.AllowanceDetails?.AwareOfBCEAmounts),
                            }
                        );
                    }
                }
            }
        }
        else if (process == ProcessType.Quote) {
            drawdown.push(
                {
                    title: "Pension option",
                    data: PensionOptionMap[contribution?.DrawdownModel?.PensionOption],
                }
            );
        }
        if (data?.DrawdownModel?.PensionOption != PensionOption.UFPLS) {
            drawdown.push({
                title: "Drawdown type",
                data: DrawdownTypeOptionsMap[contribution?.DrawdownModel?.DrawdownType],
            });
        }
        return drawdown;
    };

    const crystallisationDetailConfig = (contribution) => {
        if (!contribution) { return []; }
        const crystallisationDetails = contribution?.DrawdownModel?.CrystallisationDetails;
        const { process, isImmediateLumpSum } = props;

        return [
            {
                title: <strong>Drawdown details</strong>,
                data: " ",
            },
            {
                title: "Savings Value",
                data: MoneyDisplayHelper.getMoneyFormat({
                    Amount: contribution
                        ? calculateUnCrystallizationAmount(contribution, process, isImmediateLumpSum)
                        : crystallisationDetails?.UnCrystallisedValue?.Amount,
                    Currency: CurrencyCode.GBP,
                }),
            },
            {
                title: "Value available to move to Drawdown",
                data: MoneyDisplayHelper.getMoneyFormat({
                    Amount: contribution
                        ? calculateAvailableToCrystallise(contribution, process, isImmediateLumpSum)
                        : crystallisationDetails?.AvailableToCrystallise?.Amount,
                    Currency: CurrencyCode.GBP,
                }),
            },
            {
                title: "Available cash",
                data: MoneyDisplayHelper.getMoneyFormat(crystallisationDetails?.AvailableCash),
            },
            {
                title: "Drawdown options",
                data: CrystallisationOptionsMap[crystallisationDetails?.CrystallisationOption],
            },
            {
                title: "Drawdown amount",
                data: MoneyDisplayHelper.getMoneyFormat(crystallisationDetails?.CrystallisationAmount),
            },
        ];
    };

    const regularCrystallisationDetailConfig = (regularcrystallisationdetails) => {
        if (!regularcrystallisationdetails) { return []; }
        return [
            {
                title: <strong>Regular drawdown details</strong>,
                data: " ",
            },
            {
                title: "Regular drawdown amount",
                data: regularcrystallisationdetails.IncomeRequired?.Amount
                    ? MoneyDisplayHelper.getMoneyFormat(regularcrystallisationdetails.IncomeRequired)
                    : MoneyDisplayHelper.getMoneyFormat(regularcrystallisationdetails.Amount),
            },
            {
                title: "Specify a different initial drawdown amount",
                data: boolParse(regularcrystallisationdetails.HasADifferentInitialAmount),
            },
            {
                title: "Initial regular drawdown amount",
                data: regularcrystallisationdetails.HasADifferentInitialAmount
                    ? MoneyDisplayHelper.getMoneyFormat(regularcrystallisationdetails.InitialAmount)
                    : null,
            },
            {
                title: "Payment frequency",
                data: FrequencyArraysMap[regularcrystallisationdetails.Frequency],
            },
            {
                title: "First payment date",
                data: regularcrystallisationdetails.FirstPaymentDay
                    ? dateParser(regularcrystallisationdetails.FirstPaymentDay)
                    : dateParser(data.DrawdownModel?.RegularCrystallisationDetails.FirstPaymentDay),
            },
        ];
    };

    const regularExistingCrystallisationDetailConfig = (regularcrystallisationdetails) => {
        if (!regularcrystallisationdetails) { return []; }
        return [
            {
                title: <strong>Regular drawdown details</strong>,
                data: " ",
            },
            {
                title: "Regular drawdown amount",
                data: regularcrystallisationdetails.ProposedCrystallisation?.Amount
                    ? MoneyDisplayHelper.getMoneyFormat(regularcrystallisationdetails.ProposedCrystallisation)
                    : MoneyDisplayHelper.getMoneyFormat(regularcrystallisationdetails.Amount),
            },
            {
                title: "Specify a different initial drawdown amount",
                data: boolParse(regularcrystallisationdetails.HasADifferentInitialAmount),
            },
            {
                title: "Initial regular drawdown amount",
                data: regularcrystallisationdetails.HasADifferentInitialAmount
                    ? MoneyDisplayHelper.getMoneyFormat(regularcrystallisationdetails.InitialAmount)
                    : null,
            },
            {
                title: "Payment frequency",
                data: FrequencyArraysMap[regularcrystallisationdetails.Frequency],
            },
            {
                title: "First payment date",
                data: regularcrystallisationdetails.NextPaymentDate
                    ? dateParser(regularcrystallisationdetails.NextPaymentDate)
                    : dateParser(data.DrawdownModel?.RegularCrystallisationDetails.FirstPaymentDay),
            },
        ];
    };

    const ltaBreakdownDetailsConfig = (contribution) => {
        if (!contribution) { return []; }
        const DrawdownModel = contribution?.DrawdownModel;
        const LifetimeAllowance = DrawdownModel?.LifetimeAllowance;
        const LTADetails = [];
        LTADetails.push({
            title: <strong>LTA breakdown details</strong>,
            data: " ",
        });
        if (lessThan75) {
            LTADetails.push(
                { title: LTADetailHeading.previousLTA, data: LifetimeAllowance?.SlaPreviouslyCrystallised + "%" },
                {
                    title: LTADetailHeading.LTABefore2006,
                    data: LifetimeAllowance?.SlaBenefitsPriorTo2006 + "%",
                }
            );
        }
        LTADetails.push({
            title: LTADetailHeading.otherLTA,
            data: DrawdownModel?.SlaUsedOffPlatform + "%",
        });
        if (lessThan75) {
            LTADetails.push({
                title: LTADetailHeading.availableLTA,
                data: MoneyDisplayHelper.getMoneyFormat(LifetimeAllowance?.AvailablePersonalAllowance),
            });
        }
        if (isCappedOrFlexIOrUFPLS(DrawdownModel) && lessThan75) {
            LTADetails.push(
                {
                    title: LTADetailHeading.LTAByThisCrystallisation,
                    data: LifetimeAllowance?.SlaUsedByProposedCrystallisation + "%",
                },
                {
                    title: LTADetailHeading.LTAInTotal,
                    data: LifetimeAllowance?.TotalSlaUsed + "%",
                },
                {
                    title: LTADetailHeading.crystallisationAmountSubjectToLTAExcessCharge,
                    data: MoneyDisplayHelper.getMoneyFormat(LifetimeAllowance?.ChargeableCrystallisationPortion),
                }
            );
        }
        if (isRegualr(DrawdownModel) && lessThan75) {
            const previouslyUsed =
                LifetimeAllowance?.SlaPreviouslyCrystallised + data?.DrawdownModel.SlaUsedOffPlatform;
            LTADetails.push({
                title: LTADetailHeading.estimateLTAForInitialCrystallisation,
                data: LifetimeAllowance?.SlaUsedByProposedInitialRegularCrystallisation + "%",
            });
            LTADetails.push(
                {
                    title: LTADetailHeading.estimateLTAPerRegularCrystallisation,
                    data: LifetimeAllowance?.SlaUsedByProposedCrystallisation + "%",
                },
                {
                    title: LTADetailHeading.estimateAvailableLTAInAYear,
                    data: Math.max(0, 100 - LifetimeAllowance?.EstimatedTotalSlaUsedAfterAYear) + "%",
                },
                {
                    title: LTADetailHeading.estimateLTAUsedInAYear,
                    data: LifetimeAllowance?.EstimatedTotalSlaUsedAfterAYear - previouslyUsed + "%",
                },
                {
                    title: LTADetailHeading.estimateTotalLTAUsedInAYear,
                    data: LifetimeAllowance?.EstimatedTotalSlaUsedAfterAYear + "%",
                },
                {
                    title: LTADetailHeading.estimatedChargeableCrystallisation,
                    data: Math.max(0, LifetimeAllowance?.EstimatedTotalSlaUsedAfterAYear - 100) + "%",
                }
            );
        }
        return LTADetails;
    };

    const hasPrimaryProtection = (pensionProtection: CustomerPensionProtectionModel) => {
        return (pensionProtection?.Primary?.HasProtection)
    };

    const hasPrimaryPCLSProtection = (pensionProtection: CustomerPensionProtectionModel) => {
        return (pensionProtection?.Primary?.ProtectionAmount)
    };

    const hasEnhancedProtection = (pensionProtection: CustomerPensionProtectionModel) => {
        return (pensionProtection?.Enhanced?.HasProtection)
    };

    const hasEnhancedPCLSProtection = (pensionProtection: CustomerPensionProtectionModel) => {
        return (pensionProtection?.Enhanced?.EntitlementPercentage)
    };

    const hasTransitionalTaxFreeAllowanceCertificate = (pensionProtection: CustomerPensionProtectionModel) => {
        return (pensionProtection?.TransitionalLumpSum?.HasProtection)
    };


    const allowacneDetailsConfig = (contribution) => {
        const { process, isImmediateLumpSum } = props;
        if (!contribution) { return []; }
        const data = contribution?.DrawdownModel;
        const pensionProtection = contribution?.PensionProtection;
        const AllowanceDetails = [];

        AllowanceDetails.push({
            title: <strong>Allowance Details</strong>,
            data: " ",
        });

        AllowanceDetails.push({
            title: AllowanceDetailHeading.lsaDetails,
            data: " ",
        });

        const showLumpSumMessageForUFPLS =
            (hasPrimaryProtection(pensionProtection) && !hasPrimaryPCLSProtection(pensionProtection))
            && process == ProcessType.TopUpWithITFLS
            && data?.CrystallisationDetails?.CrystallisationAmount?.Amount > data?.CrystallisationDetails?.AvailableToCrystallise?.Amount;


        AllowanceDetails.push({
            title: AllowanceDetailHeading.lsaUsedOnOrAfter6April2024Amount,
            data: "£" + (data?.DrawdownCalculations?.LumpSumAllowance?.UsedAfter2024.Amount ?? 0).toFixed(2),
        });
        if (!hasTransitionalTaxFreeAllowanceCertificate(pensionProtection) || (hasEnhancedProtection(pensionProtection) && hasEnhancedPCLSProtection(pensionProtection))
            | (hasPrimaryProtection(pensionProtection) && hasPrimaryPCLSProtection(pensionProtection))) {
            AllowanceDetails.push({
                title: AllowanceDetailHeading.lsaUsedBefore6April2006Amount,
                data: "£" + (data?.DrawdownCalculations?.LumpSumAllowance?.UsedBefore2006.Amount ?? 0).toFixed(2),
            });

        }
        if ((!hasTransitionalTaxFreeAllowanceCertificate(pensionProtection) && data?.KnowBceAllowanceAmount) || (hasEnhancedProtection(pensionProtection) && hasEnhancedPCLSProtection(pensionProtection))
            || (hasPrimaryProtection(pensionProtection) && hasPrimaryPCLSProtection(pensionProtection) && data.KnowBceAllowanceAmount)) {
            AllowanceDetails.push({
                title: AllowanceDetailHeading.lsaOtherUsedAsAt5April2024Amount,
                data: "£" + (data?.DrawdownCalculations?.LumpSumAllowance?.UsedOffPlatformBefore2024.Amount ?? 0).toFixed(2),
            });
        }

        AllowanceDetails.push({
            title: AllowanceDetailHeading.lsaOtherUsedOnOrAfter6April2024Amount,
            data: "£" + (data?.DrawdownCalculations?.LumpSumAllowance?.UsedOffPlatformAfter2024.Amount ?? 0).toFixed(2),
        });

        if (isRegualr(data)) {
            var ititialCrystallisation =
                data.DrawdownCalculations?.LumpSumAllowance?.UsedByInitialCrystallisation?.Amount != null
                    ? data?.DrawdownCalculations?.LumpSumAllowance?.UsedByInitialCrystallisation.Amount.toFixed(2)
                    : 0;
            AllowanceDetails.push(
                {
                    title: AllowanceDetailHeading.lsaUsedByInitialCrystallisation,
                    data: "£" + ititialCrystallisation,
                },
                {
                    title: AllowanceDetailHeading.lsaUsedByRegularCrystallisation,
                    data: "£" + (data?.DrawdownCalculations?.LumpSumAllowance?.UsedByCrystallisation.Amount ?? 0).toFixed(2),
                },
                {
                    title: AllowanceDetailHeading.lsaUsedFollowingBycrystallisation,
                    data: "£" + (data?.DrawdownCalculations?.LumpSumAllowance?.UsedTotal.Amount ?? 0).toFixed(2),
                }
            );
        } else {
            AllowanceDetails.push(
                {
                    title: AllowanceDetailHeading.lsaUsedBycrystallisation,
                    data: "£" + (data?.DrawdownCalculations?.LumpSumAllowance?.UsedByCrystallisation.Amount ?? 0).toFixed(2),
                },
                {
                    title: AllowanceDetailHeading.lsaUsedFollowingBycrystallisation,
                    data: "£" + (data?.DrawdownCalculations?.LumpSumAllowance?.UsedTotal.Amount ?? 0).toFixed(2),
                }
            );
        }

        AllowanceDetails.push({
            title: AllowanceDetailHeading.lsdbaDetails,
            data: " ",
        });


        if (!hasTransitionalTaxFreeAllowanceCertificate(pensionProtection) || (hasEnhancedProtection(pensionProtection) && hasEnhancedPCLSProtection(pensionProtection))
            || (hasPrimaryProtection(pensionProtection) && hasPrimaryPCLSProtection(pensionProtection))) {
            AllowanceDetails.push({
                title: AllowanceDetailHeading.lsdbaUsedLSDBAAsAt5April2024Amount,
                data: "£" + (data?.DrawdownCalculations?.LumpSumDeathBenefitAllowance?.UsedBefore2024.Amount ?? 0).toFixed(2),
            });
        }

        AllowanceDetails.push({
            title: AllowanceDetailHeading.lsdbaUsedLSDBAOnOrAfter6April2024Amount,
            data: "£" + (data?.DrawdownCalculations?.LumpSumDeathBenefitAllowance?.UsedAfter2024.Amount ?? 0).toFixed(2),
        });

        if (!hasTransitionalTaxFreeAllowanceCertificate(pensionProtection) || (hasEnhancedProtection(pensionProtection) && hasEnhancedPCLSProtection(pensionProtection))
            || (hasPrimaryProtection(pensionProtection) && hasPrimaryPCLSProtection(pensionProtection))) {
            AllowanceDetails.push({
                title: AllowanceDetailHeading.lsdbaUsedLSDBABefore6April2006Amount,
                data: "£" + (data?.DrawdownCalculations?.LumpSumDeathBenefitAllowance?.UsedBefore2006.Amount ?? 0).toFixed(2),
            });

        }

        if ((!hasTransitionalTaxFreeAllowanceCertificate(pensionProtection) && data?.AllowanceDetails?.HasOffPlatformBcePriorApril2024 && data?.AllowanceDetails?.AwareOfBCEAmounts) || (hasEnhancedProtection(pensionProtection) && hasEnhancedPCLSProtection(pensionProtection))
            || (hasPrimaryProtection(pensionProtection) && hasPrimaryPCLSProtection(pensionProtection) && data?.AllowanceDetails?.HasOffPlatformBcePriorApril2024 && data?.AllowanceDetails?.AwareOfBCEAmounts)) {
            AllowanceDetails.push(
                {
                    title: AllowanceDetailHeading.lsdbaOtherLSDBAUsedAsAt5April2024Amount,
                    data: "£" + (data?.DrawdownCalculations?.LumpSumDeathBenefitAllowance?.UsedOffPlatformBefore2024?.Amount ?? 0).toFixed(2),
                },
                {
                    title: AllowanceDetailHeading.isPartOfUsedSIHBefore75,
                    data: boolParse(data?.AllowanceDetails?.LumpSumDeathBenefitAllowanceUsedSeriousIllHealthBefore75)
                }
            );
        }

        AllowanceDetails.push({
            title: AllowanceDetailHeading.lsdbaOtherLSDBAUsedOnOrAfter6April2024Amount,
            data: "£" + (data?.DrawdownCalculations?.LumpSumDeathBenefitAllowance?.UsedOffPlatformAfter2024?.Amount ?? 0).toFixed(2),
        });
        if ((hasEnhancedProtection(pensionProtection) && !hasEnhancedPCLSProtection(pensionProtection))) {
            AllowanceDetails.push({
                title: AllowanceDetailHeading.hasTakenIllHealthLumpSum,
                data: " ",
            });
        }

        if (isRegualr(data)) {
            var lsdbaUsedByInitialCrystallisation = data?.DrawdownCalculations?.LumpSumDeathBenefitAllowance
                ?.UsedByInitialCrystallisation?.Amount
                ? data?.DrawdownCalculations?.LumpSumDeathBenefitAllowance?.UsedByInitialCrystallisation?.Amount.toFixed(2) : 0;
            AllowanceDetails.push(
                {
                    title: AllowanceDetailHeading.lsdbaUsedByInitialCrystallisation,
                    data: "£" + lsdbaUsedByInitialCrystallisation,
                },
                {
                    title: AllowanceDetailHeading.lsdbaUsedByRegularCrystallisation,
                    data: "£" + (data?.DrawdownCalculations?.LumpSumDeathBenefitAllowance?.UsedByCrystallisation.Amount ?? 0).toFixed(2),
                },
                {
                    title: AllowanceDetailHeading.lsdbaUsedFollowingBycrystallisation,
                    data: "£" + (data?.DrawdownCalculations?.LumpSumDeathBenefitAllowance?.UsedTotal.Amount ?? 0).toFixed(2),
                }
            );
        } else
            AllowanceDetails.push(
                {
                    title: AllowanceDetailHeading.lsdbaUsedBycrystallisation,
                    data: "£" + (data?.DrawdownCalculations?.LumpSumDeathBenefitAllowance?.UsedByCrystallisation.Amount ?? 0).toFixed(2),
                },
                {
                    title: AllowanceDetailHeading.lsdbaUsedFollowingBycrystallisation,
                    data: "£" + (data?.DrawdownCalculations?.LumpSumDeathBenefitAllowance?.UsedTotal.Amount ?? 0).toFixed(2),
                }
            );

        return AllowanceDetails;
    }

    const taxFreeLumpSumPaymentConfig = (contribution) => {
        if (!contribution) { return []; }
        const DrawdownModel = contribution?.DrawdownModel;
        const taxFreeLumpSum = [];
        taxFreeLumpSum.push({
            title: <strong>TFLS payment</strong>,
            data: " ",
        });
        if (DrawdownModel?.PensionOption == PensionOption.Drawdown) {
            taxFreeLumpSum.push({
                title: "Tax free lump sum payment",
                data: MoneyDisplayHelper.getMoneyFormat(DrawdownModel?.TaxFreeLumpSum),
            });
        }
        if (DrawdownModel?.PensionOption == PensionOption.UFPLS) {
            taxFreeLumpSum.push({
                title: "Tax-free element of uncrystallised funds pension lump sum",
                data: MoneyDisplayHelper.getMoneyFormat(DrawdownModel?.UfplsWithdrawal?.TaxFreePortion),
            });
            taxFreeLumpSum.push({
                title: "Taxable element of uncrystallised funds pension lump sum",
                data: MoneyDisplayHelper.getMoneyFormat(DrawdownModel?.UfplsWithdrawal?.TaxablePortion),
            });
        }
        if (showBenefitTable(DrawdownModel, lessThan75, hasEnhancedPensionProtection)) {
            taxFreeLumpSum.push({
                title: "Gross excess benefits - Lump sum",
                data: DrawdownModel?.GrossExcessBenefitLumpSum + "%",
            });
            taxFreeLumpSum.push({
                title: "Gross excess benefits - Provide additional income",
                data: DrawdownModel?.GrossExcessBenefitProvideAdditionalIncome + "%",
            });
        }
        return taxFreeLumpSum;
    };

    const drawdownAdviserChargesConfig = (contribution) => {
        if (!contribution) { return []; }
        const DrawdownModel = contribution?.DrawdownModel;
        const drawdownAdviserCharges = [];
        drawdownAdviserCharges.push({
            title: <strong>Drawdown Adviser Charge</strong>,
            data: " ",
        });
        if (DrawdownModel?.DrawdownType != DrawdownType.Regular) {
            drawdownAdviserCharges.push({
                title: "Initial",
                data:
                    DrawdownModel?.DrawdownAdviserCharge?.DrawdownInitialAdviserCharge?.Type === MoneyOrPercentageType.Money
                        ? MoneyDisplayHelper.getMoneyFormat(
                            DrawdownModel?.DrawdownAdviserCharge?.DrawdownInitialAdviserCharge?.Money
                        )
                        : DrawdownModel?.DrawdownAdviserCharge?.DrawdownInitialAdviserCharge?.Percentage + "%",
            });
        }
        drawdownAdviserCharges.push({
            title: "Ongoing",
            data:
                DrawdownModel.DrawdownAdviserCharge.OngoingAdviserCharge.Type === MoneyOrPercentageType.Money
                    ? MoneyDisplayHelper.getMoneyFormat(DrawdownModel.DrawdownAdviserCharge.OngoingAdviserCharge.Money)
                    : DrawdownModel.DrawdownAdviserCharge.OngoingAdviserCharge.Percentage != null
                        ? DrawdownModel.DrawdownAdviserCharge.OngoingAdviserCharge.Percentage + "%"
                        : "NA",
        });
        drawdownAdviserCharges.push({
            title: "Take the charge from:",
            data: ChargeFromMap[contribution?.OngoingAdviceCharge?.SourceType && contribution.OngoingAdviceCharge.SourceType !== SourceType.NotSpecified
                ? contribution.OngoingAdviceCharge.SourceType
                : SourceType.ProductCash]

        });

        return drawdownAdviserCharges;
    };

    const drawdownPriceLockConfig = (contribution) => {
        if (!contribution) { return []; }
        const DrawdownModel = contribution?.DrawdownModel;
        const drawdownPriceLock = [];
        drawdownPriceLock.push({
            title: <strong>Drawdown price lock</strong>,
            data: " ",
        });
        drawdownPriceLock.push({
            title: "Do you want to apply the Drawdown Price Lock?",
            data: boolParse(DrawdownModel?.DrawdownPriceLock),
        });
        if (DrawdownModel?.DrawdownPriceLock) {
            drawdownPriceLock.push({
                title: "Drawdown price lock",
                data: Math.round(DrawdownModel?.DrawdownPriceLockPercentage * 100) / 100,
            });
        }
        return drawdownPriceLock;
    };
    const chargesOnIllustrationConfig = (contribution) => {
        if (!contribution) { return []; }
        const chargesOnIllustration = [];
        chargesOnIllustration.push({
            title: <strong>Charges on illustration</strong>,
            data: " ",
        });
        chargesOnIllustration.push({
            title: "Do you want to exclude adviser charges from illustration?",
            data: boolParse(contribution?.ChargesExtraInfo?.ExcludeChargesOnIllustration),
        });
        return chargesOnIllustration;
    };

    const assetsToSellConfig = (contribution: PensionBase) => {
        if (!contribution) { return []; }
        const DrawdownModel = contribution?.DrawdownModel;
        const assetsToSell = [];
        assetsToSell.push({
            title: <strong>Choose assets to sell</strong>,
            data: " ",
        });
        assetsToSell.push({
            title: "Choose asset selection method",
            data: DrawdownModel?.IsAcrossAllAssets ? "Proportionally across all assets" : "From specified assets",
        });
        if (!DrawdownModel?.IsAcrossAllAssets) {
            const allPensionTransfersInvestments = getAllPensionTransfersInvestments(contribution);

            for (const el of DrawdownModel?.PensionCommencementLumpSumAllocations ?? []) {
                let name = data?.PreRetirementInstrumentSelections?.Instruments?.find((selection) => {
                    return selection?.Instrument.ProductId === el.ProductId;
                })?.Instrument?.Name;

                if (!name) {
                    name = allPensionTransfersInvestments.find((investment) => {
                        return investment?.ProductId === el.ProductId;
                    })?.DisplayName;
                }
                assetsToSell.push({
                    title: <div className="pl-10">{name}</div>,
                    data: <div className="pl-10">{el?.Percentage}%</div>,
                });
            }
        }
        return assetsToSell;
    };
    const bankAccountDetailsConfig = (contribution) => {
        if (!contribution) { return []; }
        const { IncomePaymentAllocations } = contribution?.DrawdownModel?.Income;
        if (!IncomePaymentAllocations) { return []; }
        const bankDetails = [];
        bankDetails.push({
            title: <strong>Bank account details</strong>,
            data: " ",
        });
        IncomePaymentAllocations.map((bankAccount) => {
            bankDetails.push({
                title: "Bank account",
                data: bankAccountParseWithHolderName(
                    bankAccount.BankAccountId,
                    modelData?.AllBankAccounts || [],
                    bankAccount.Destination
                ),
            });
            bankDetails.push({
                title: "Portion of PCLS",
                data: bankAccount.PclsAllocation + "%",
            });
            bankDetails.push({
                title: "Proportion of income",
                data: bankAccount.Allocation + "%",
            });
        });
        return bankDetails;
    };

    const switchInvestmentAllocations = (data: PensionBase) => {
        if (!data || !data?.DrawdownModel?.SwitchInvestments) {
            return [];
        }

        const treasuredAssetsIds: number[] = data?.DrawdownModel?.TreasuredAssets ? data?.DrawdownModel?.TreasuredAssetsInstruction?.ProductIds : [];
        const switchInstructionModel = [];

        switchInstructionModel.push({
            title: <strong>Switch Investments</strong>,
            data: <SwitchInvestmentsTable
                switchInvestmentsModel={data?.DrawdownModel?.SwitchInstructionModel}
                treassuredProductIds={treasuredAssetsIds}
            />,
        });

        return switchInstructionModel;
    };

    const title = process == ProcessType.TakePensionBenefits ? "Pension benefit details" : "Drawdown details";
    const tye2024 = TaxYearHelper.GetCurrentTaxYear() >= 2024
    return (
        <Accordion label={title} isOpen={false} onEdit={props.onEditAction}>
            <div className="row">
                {process == ProcessType.EditIncomeDrawdown && (
                    <div className="col-12 col-sm-fluid">
                        <h3>Before</h3>
                        <Typography {...prepareTypography(drawdownConfig(existingContribution), 1, "common")} />
                        <br />
                        {data?.DrawdownModel?.DrawdownType === DrawdownType.Regular &&
                            data?.DrawdownModel?.PensionOption != PensionOption.UFPLS ? (
                            <Typography {...prepareTypography(regularExistingCrystallisationDetailConfig(existingregularcrystallisationdetails), 1, "common")}></Typography>
                        ) : (
                            <Typography {...prepareTypography(crystallisationDetailConfig(existingContribution), 1, "common")}></Typography>
                        )}
                        <br />
                        {
                            !tye2024 && (<>
                                <Typography {...prepareTypography(ltaBreakdownDetailsConfig(existingContribution), 1, "common")}></Typography>
                                <br /></>)
                        }
                        <Typography {...prepareTypography(allowacneDetailsConfig(existingContribution), 1, "common")}></Typography>
                        <br />
                        <Typography {...prepareTypography(taxFreeLumpSumPaymentConfig(existingContribution), 1, "common")}></Typography>
                        <br />

                        <Typography {...prepareTypography(drawdownAdviserChargesConfig(existingContribution), 1, "common")}></Typography>

                        {props.isImmediateLumpSum && (
                            <CrystallizationPerContribution
                                data={existingContribution?.DrawdownModel}
                                onChange={() => { }}
                                onRef={() => { }}
                                modelData={existingContribution}
                                process={props.process}
                            />
                        )}

                        {props.showDrawdownPriceLock && (
                            <Typography {...prepareTypography(drawdownPriceLockConfig(existingContribution), 1, "common")} />
                        )}
                        <br />
                        {props.showAssetsToSell && (
                            <Typography {...prepareTypography(assetsToSellConfig(existingContribution), 1, "common")}></Typography>
                        )}
                        {data?.DrawdownModel?.PensionOption == PensionOption.UFPLS && (
                            <Typography {...prepareTypography(bankAccountDetailsConfig(existingContribution), 1, "common")} />
                        )}
                    </div>
                )}
                <div className="col-12 col-sm-fluid">
                    {process == ProcessType.EditIncomeDrawdown && (
                        <h3>After</h3>
                    )}
                    <Typography {...prepareTypography(drawdownConfig(data), 1, "common")} />
                    <br />
                    {data?.DrawdownModel?.DrawdownType === DrawdownType.Regular &&
                        data?.DrawdownModel?.PensionOption != PensionOption.UFPLS ? (
                        <Typography {...prepareTypography(regularCrystallisationDetailConfig(data.DrawdownModel?.RegularCrystallisationDetails), 1, "common")}></Typography>
                    ) : (
                        <Typography {...prepareTypography(crystallisationDetailConfig(data), 1, "common")}></Typography>
                    )}
                    <br />
                    {
                        !tye2024 && (<>
                            <Typography {...prepareTypography(ltaBreakdownDetailsConfig(data), 1, "common")}></Typography>
                            <br /></>)
                    }

                    <Typography {...prepareTypography(allowacneDetailsConfig(data), 1, "common")}></Typography>
                    <br />
                    <Typography {...prepareTypography(taxFreeLumpSumPaymentConfig(data), 1, "common")}></Typography>
                    <br />

                    <Typography {...prepareTypography(drawdownAdviserChargesConfig(data), 1, "common")}></Typography>

                    {props.isImmediateLumpSum && (
                        <CrystallizationPerContribution
                            data={data?.DrawdownModel}
                            onChange={() => { }}
                            onRef={() => { }}
                            modelData={data}
                            process={props.process}
                        />
                    )}

                    <Typography {...prepareTypography(chargesOnIllustrationConfig(data), 1, "common")} />

                    {props.showDrawdownPriceLock && (
                        <Typography {...prepareTypography(drawdownPriceLockConfig(data), 1, "common")} />
                    )}
                    <br />
                    {props.showAssetsToSell && (
                        <Typography {...prepareTypography(assetsToSellConfig(data), 1, "common")}></Typography>
                    )}
                    {data?.DrawdownModel?.PensionOption == PensionOption.UFPLS && (
                        <Typography {...prepareTypography(bankAccountDetailsConfig(data), 1, "common")} />
                    )}

                    <Typography {...prepareTypography(switchInvestmentAllocations(data), 1, "common")}></Typography>
                    <br />
                </div>
            </div>
        </Accordion>
    );
};
